import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useCourseDetailsStore } from '@/stores/course-details'
import { useCourseStore } from '@/stores/course'
import { formatCurriculumOverview } from '@/utils/helpers'
import {
  courseDetailsFakeLearningObjectives,
  courseDetailsFakeAuthor,
} from '@/domain/providers/course-details.provider'
import { useIndexRoutes } from '@/composables/locale/useIndexRoutes'

export function useCourseDetails() {
  const route = useRoute()
  const { t } = useI18n()
  const { buildPath, navigate } = useIndexRoutes()
  const courseDetailsStore = useCourseDetailsStore()
  const courseStore = useCourseStore()
  const { display, loading, error, hasCourseDetails, hasError } = storeToRefs(courseDetailsStore)

  const slug = computed(() => (route.params.slug as string) ?? '')
  const course = computed(() => display.value?.course ?? null)
  const category = computed(
    () => (route.params.category as string) || course.value?.category || 'biblical',
  )
  const curriculumOverview = computed(() =>
    formatCurriculumOverview(display.value?.curriculumOverviewParts ?? null, t),
  )
  const curriculumSections = computed(() => display.value?.curriculumSections ?? [])
  const learningObjectives = computed(() => display.value?.learningObjectives ?? [])
  const authorData = computed(() => display.value?.authorData ?? { name: '' })

  const backHref = computed(() => buildPath({ category: category.value }))

  function goBack(_e: MouseEvent) {
    navigate({ category: category.value })
  }

  async function loadCourseAndLessons() {
    if (!slug.value) return

    courseStore.resetCategoryList()
    await courseDetailsStore.fetchCourseDetailsBySlug(slug.value, {
      defaultSectionTitle: t('courseDetails.curriculum.title'),
      defaultLearningObjectives: courseDetailsFakeLearningObjectives,
      defaultAuthor: { name: '' },
    })
  }

  return {
    slug,
    course,
    category,
    backHref,
    curriculumOverview,
    curriculumSections,
    learningObjectives,
    authorData,
    loading,
    error,
    errorText: computed(() => error.value?.message ? ` ${error.value.message}` : ""),
    hasCourseDetails,
    hasError,
    goBack,
    loadCourseAndLessons,
  }
}
